import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const BASE_TITLE = 'AbodeLMS';

/**
 * Routes are declared in the order they appear in the nav so
 * `meta.title` reads the same way a learner would refer to each page.
 * A catch-all at the bottom redirects dead legacy URLs (e.g. the old
 * /learner/proctor stub) to the home dashboard rather than rendering
 * an empty shell.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, title: 'Sign in' },
  },
  {
    path: '/',
    component: () => import('@/components/AppShell.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/HomeView.vue'),
        meta: { title: 'Home' },
      },
      {
        path: 'my-courses',
        name: 'my-courses',
        component: () => import('@/views/MyCoursesView.vue'),
        meta: { title: 'My Courses' },
      },
      {
        path: 'my-certificates',
        name: 'my-certificates',
        component: () => import('@/views/MyCertificatesView.vue'),
        meta: { title: 'My Certificates' },
      },
      {
        path: 'my-purchases',
        name: 'my-purchases',
        component: () => import('@/views/MyPurchasesView.vue'),
        meta: { title: 'My Purchases' },
      },
      {
        path: 'my-files',
        name: 'my-files',
        component: () => import('@/views/MyFilesView.vue'),
        meta: { title: 'My Files' },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: { title: 'Profile' },
      },
      {
        path: 'group-certificates',
        name: 'group-certificates',
        component: () => import('@/views/GroupCertificateAccessView.vue'),
        meta: { title: 'Group Certificates' },
      },
      {
        // Catch any unknown route under the shell and send the learner
        // home. Apache already serves index.html for anything under
        // /learner/, so this handles removed or mistyped routes.
        path: ':pathMatch(.*)*',
        redirect: { name: 'home' },
      },
    ],
  },
];

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(async (to) => {
  if (to.meta.public) return true;

  const auth = useAuthStore();
  if (auth.isAuthenticated) return true;

  const ok = await auth.checkSession();
  if (ok) return true;

  return { name: 'login', query: { redirect: to.fullPath } };
});

router.afterEach((to) => {
  const title = (to.meta.title as string | undefined) || '';
  document.title = title ? `${title} · ${BASE_TITLE}` : BASE_TITLE;
});
